<template>
  <div class="builder-page">
    <div class="wrap">
      <header class="builder-head">
        <a class="brand" href="/" aria-label="Foodrazor">
          <img src="../../../assets/Foodrazor-logo.svg" alt="Foodrazor">
        </a>
        <div class="eyebrow"><span class="dot"></span>Invoice automation</div>
        <h1 class="h-display">
          Need more invoices? <span class="hl">Add invoice automation volume.</span>
        </h1>
        <p class="lede">
          Set the number of invoice pages you want automated each month. The more you process,
          the lower your per-page rate — your cost updates live on the right.
        </p>

        <div class="billing-toggle" role="tablist" aria-label="Billing period">
          <button
            v-for="b in copy.billing"
            :key="b.id"
            class="billing-opt"
            :class="{ active: b.id === state.billing }"
            role="tab"
            :aria-selected="String(b.id === state.billing)"
            type="button"
            @click="setBilling(b.id)"
          >
            {{ b.label }}
            <span v-if="b.id === 'year' && savingPercent > 0" class="save">Save {{ savingPercent }}%</span>
          </button>
        </div>
      </header>

      <p v-if="loading" class="state-note">Loading pricing…</p>
      <p v-else-if="loadError" class="state-note error">{{ loadError }}</p>

      <div v-else class="builder-grid">

        <!-- ============ LEFT: VOLUME ============ -->
        <div class="steps">
          <section class="step">
            <div class="step-head">
              <div>
                <h2 class="step-title">{{ copy.volStep.title }}</h2>
                <p class="step-sub">{{ copy.volStep.subtitle }}</p>
              </div>
            </div>

            <div class="vol-card">
              <div class="vol-top">
                <div class="vol-inputwrap">
                  <input
                    class="vol-input"
                    :class="{ invalid: belowMinimum }"
                    type="number"
                    inputmode="numeric"
                    :min="minimumPages"
                    :max="copy.volume.max"
                    :step="copy.volume.step"
                    :value="state.pages"
                    :aria-label="copy.volStep.title"
                    :aria-invalid="String(belowMinimum)"
                    @input="setVolume($event.target.value)"
                  >
                  <span class="vol-input-lbl"><strong>pages</strong><br>/ month</span>
                  <span v-if="minimumPages > 0" class="vol-min-note">
                    {{ num(includedPages) }} included — minimum
                  </span>
                </div>
                <div class="vol-rate">{{ averageRateLabel }}</div>
              </div>

              <p v-if="belowMinimum" class="vol-error" role="alert">
                {{ num(includedPages) }} pages are already included — choose at least
                {{ num(minimumPages) }} to add automation volume.
              </p>

              <input
                class="vol-slider"
                type="range"
                :min="minimumPages"
                :max="copy.volume.sliderMax"
                :step="copy.volume.step"
                :value="sliderValue"
                :aria-label="copy.volStep.title"
                @input="setVolume($event.target.value)"
              >

              <div class="vol-presets">
                <button
                  v-for="preset in presets"
                  :key="preset"
                  class="vol-preset"
                  type="button"
                  @click="setVolume(preset)"
                >
                  {{ num(preset) }}
                </button>
              </div>

              <div class="vol-tiers">
                <p v-if="!breakdown.lines.length" class="vol-tiers-h no-margin">
                  Set a volume to see your per-page cost.
                </p>
                <template v-else>
                  <p class="vol-tiers-h">
                    Your rate, tier by tier
                    <span v-if="discount > 0" class="tiers-note">{{ discount }}% discount applied</span>
                  </p>
                  <div v-for="line in breakdown.lines" :key="line.from" class="vt-row">
                    <span class="vt-band">{{ bandLabel(line) }} · {{ rateLabel(line.rate) }}</span>
                    <span class="vt-cost">{{ money(line.cost) }}</span>
                  </div>
                </template>
              </div>
            </div>
          </section>
        </div>

        <!-- ============ RIGHT: SUMMARY ============ -->
        <aside class="summary">
          <div class="summary-card">
            <div class="summary-head">
              <h3>Your subscription</h3>
              <p>{{ summaryPlanLine }}</p>
            </div>

            <div class="summary-body">
              <div class="sec-label">Monthly</div>
              <div class="li" :class="{ muted: state.pages === 0 }">
                <span class="li-name">
                  {{ itemName }}
                  <span class="li-sub">
                    {{ num(state.pages) }} pages/mo
                    <template v-if="!isMonthly && monthlyCost > 0 && savingPercent > 0">
                      · <span class="save">{{ savingPercent }}% annual rate</span>
                    </template>
                  </span>
                </span>
                <span class="li-amt">{{ money(monthlyCost) }}/mo</span>
              </div>

              <div class="summary-divider"></div>

              <div class="subtotal">
                <span class="st-name">Monthly subtotal</span>
                <span class="st-amt">{{ money(monthlyCost) }}/mo</span>
              </div>

              <div class="total-block">
                <div class="total-row">
                  <div class="t-label">
                    {{ currentBilling.totalLabel }}
                    <small>plus applicable taxes</small>
                  </div>
                  <div class="t-amt">
                    {{ money(headlineAmount) }}<span class="per"> {{ currentBilling.cycleLabel }}</span>
                  </div>
                </div>

                <div v-if="!isMonthly && annualSaving > 0" class="annual-note">
                  That's {{ money(monthlyCost) }}/mo billed annually ·
                  <span class="save">save {{ money0(annualSaving) }}/yr</span> on your automations.
                </div>
              </div>
            </div>

            <div class="summary-foot">
              <button
                class="btn btn-primary"
                type="button"
                :disabled="!cart || submitting"
                @click="onCheckout"
              >
                {{ submitting ? 'Redirecting…' : 'Continue to checkout' }}
                <svg class="btn-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"
                     stroke-linecap="round" stroke-linejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </button>

              <p v-if="checkoutError" class="state-note error small">{{ checkoutError }}</p>
              <p v-else-if="belowMinimum" class="state-note small">
                Choose at least {{ num(minimumPages) }} pages to continue.
              </p>

              <router-link class="btn-skip" :to="{ name: 'venue-home' }">
                Skip — I don't need more invoices
              </router-link>

              <div class="trust">
                <span><span class="check"><tick-icon /></span> Cancel or change volume anytime</span>
                <span><span class="check"><tick-icon /></span> Secure checkout via Stripe</span>
              </div>
            </div>
          </div>

          <p class="mini-print">
            Unused automations carry over month to month. Questions about volume?
            <a :href="topUpsHref">See top-ups &amp; automation pricing</a>.
          </p>
        </aside>

      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';

// Presentation copy that is not priced lives here; every price comes from the API.
const COPY = {
  volStep: {
    title: 'Invoice automation volume',
    subtitle: 'Total invoice pages you want automated each month.',
  },
  billing: [
    { id: 'month', label: 'Monthly', cycleLabel: '/month', totalLabel: 'Recurring total' },
    { id: 'year', label: 'Yearly', cycleLabel: '/year', totalLabel: 'Annual total' },
  ],
  // Volume input bounds. The slider covers the common range; the number input
  // allows the full range for high-volume customers.
  volume: { max: 20000, sliderMax: 1500, step: 10, fallbackPages: 60 },
  defaults: { billing: 'month' },
  itemNameFallback: 'Invoice Automation',
};

const TickIcon = {
  name: 'TickIcon',
  render(h) {
    return h('svg', {
      attrs: {
        viewBox: '0 0 24 24',
        fill: 'none',
        stroke: 'currentColor',
        'stroke-width': '3.4',
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
      },
    }, [h('polyline', { attrs: { points: '20 6 9 17 4 12' } })]);
  },
};

export default {
  name: 'InvoiceAutomationBuilder',

  components: { TickIcon },

  data() {
    return {
      copy: COPY,
      // { month: { key, name, tiers, presets }, year: { ... } }
      automation: {},
      state: {
        billing: COPY.defaults.billing,
        pages: 0,
      },
      loading: true,
      loadError: '',
      submitting: false,
      checkoutError: '',
      topUpsHref: 'https://www.foodrazor.com/invoice-top-ups',
    };
  },

  computed: {
    currentBilling() {
      return this.copy.billing.find(b => b.id === this.state.billing) || this.copy.billing[0];
    },

    isMonthly() {
      return this.state.billing === 'month';
    },

    currentItem() {
      return this.automation[this.state.billing] || null;
    },

    itemName() {
      return (this.currentItem && this.currentItem.name) || this.copy.itemNameFallback;
    },

    // list tiers as the API sent them, before any discount
    listTiers() {
      return (this.currentItem && this.currentItem.tiers) || [];
    },

    discount() {
      return (this.currentItem && this.currentItem.discount) || 0;
    },

    // lowest volume the customer may select
    minimumPages() {
      return (this.currentItem && this.currentItem.minimum) || 0;
    },

    // pages already included with the plan — one below the first billable volume
    includedPages() {
      return Math.max(0, this.minimumPages - 1);
    },

    // the entered volume is not allowed: too low to add any automation
    belowMinimum() {
      return this.minimumPages > 0 && this.state.pages < this.minimumPages;
    },

    // what the customer actually pays: list rates with the interval's discount applied
    tiers() {
      return this.applyDiscount(this.listTiers, this.discount);
    },

    // presets below the minimum are unreachable, so they are not offered
    presets() {
      const all = (this.currentItem && this.currentItem.presets) || [];
      return all.filter(p => p >= this.minimumPages);
    },

    // graduated tier walk for the selected interval
    breakdown() {
      return this.computeCost(this.state.pages, this.tiers);
    },

    monthlyCost() {
      return this.breakdown.cost;
    },

    // cost at the current volume if billed monthly — the baseline for savings
    monthlyBaselineCost() {
      return this.costFor('month', this.state.pages);
    },

    /**
     * Yearly saving, as a percentage.
     * If the yearly row carries an explicit `options.discount`, that IS the
     * saving — no derivation. Otherwise fall back to comparing what the two
     * intervals cost at the same volume.
     */
    savingPercent() {
      const yearly = this.automation.year;
      if (!yearly) return 0;
      if (yearly.discount > 0) return yearly.discount;

      const pages = this.state.pages > 0 ? this.state.pages : this.copy.volume.fallbackPages;
      const monthlyCost = this.costFor('month', pages);
      if (!monthlyCost) return 0;

      return Math.round((1 - this.costFor('year', pages) / monthlyCost) * 100);
    },

    annualSaving() {
      if (this.isMonthly) return 0;
      return Math.max(0, (this.monthlyBaselineCost - this.monthlyCost) * 12);
    },

    headlineAmount() {
      return this.isMonthly ? this.monthlyCost : this.monthlyCost * 12;
    },

    sliderValue() {
      return Math.max(this.minimumPages, Math.min(this.state.pages, this.copy.volume.sliderMax));
    },

    marginalRateLabel() {
      const { lines } = this.breakdown;

      if (this.state.pages > 0 && lines.length) {
        return `Your marginal rate: ${this.rateLabel(lines[lines.length - 1].rate)}`;
      }

      const [first] = this.tiers;
      return `Starting at ${this.rateLabel(first ? first.rate : 0)}`;
    },

   averageRateLabel() {
      const { lines } = this.breakdown;
      const avgRate = this.breakdown.cost / this.state.pages
      if (this.state.pages > 0 && lines.length) {
        return `Your average rate: ${this.rateLabel(avgRate * 100)}`;
      }

      const [first] = this.tiers;
      return `Starting at ${this.rateLabel(first ? first.rate : 0)}`;
    },

    summaryPlanLine() {
      return `${this.itemName} · billed ${this.currentBilling.label.toLowerCase()}`;
    },

    /**
     * Quantity is the chosen MONTHLY page volume for both intervals. The yearly
     * Stripe price keeps its tier `up_to` bounds in monthly pages and carries the
     * ×12 in the per-page rate, so the quantity must not be annualised here.
     *
     * Returns null when the selection is invalid, which is what disables the CTA.
     */
    cart() {
      const item = this.currentItem;
      if (!item || this.state.pages <= 0 || this.belowMinimum) return null;

      return {
        invoices: {
          key: item.key,
          quantity: this.state.pages,
        },
      };
    },
  },

  created() {
    this.loadConfig();
  },

  methods: {
    ...mapActions('stripe', [
      'venueFetchStripePrices',
      'venueCreateCheckoutSession',
    ]),

    /**
     * GET the subscription config. Same endpoint as the plan builder — this page
     * only consumes the rows where type === 'invoices'.
     */
    async fetchSubscriptionConfig() {
      const { data } = await this.venueFetchStripePrices({});
      return data;
    },

    /**
     * POST the cart and return the Stripe Checkout session ({ url }).
     */
    async postCheckoutSession(cart) {
      const { data } = await this.venueCreateCheckoutSession({ data: cart });
      return (data && data.data) || data;
    },

    // =========================================================================
    // LOADING / INIT
    // =========================================================================

    async loadConfig() {
      this.loading = true;
      this.loadError = '';

      try {
        const config = await this.fetchSubscriptionConfig();
        this.parseConfig(config);

        const missing = this.copy.billing.filter((b) => {
          const item = this.automation[b.id];
          return !item || !item.tiers.length;
        });
        if (missing.length) {
          throw new Error('Invoice automation tiers missing from the pricing API.');
        }

        this.applyDefaults();
      } catch (error) {
        this.loadError = 'Pricing configuration could not be loaded.';
        // eslint-disable-next-line no-console
        console.error('[invoice-builder] init failed', error);
      } finally {
        this.loading = false;
      }
    },

    /**
     * Invoice automation rows are metered, so `amount` is null. The price lives in
     * `options.tiers` — a graduated list of { up_to, amount }, where `amount` is
     * cents per page and `up_to: null` means "everything above the previous tier".
     * `options.presets` holds the volume shortcut buttons.
     */
    parseConfig(config) {
      const rows = (config && config.data) || [];
      const automation = {};

      rows.forEach((row) => {
        if (row.type !== 'invoices' || !row.interval) return;

        const options = row.options || {};

        automation[row.interval] = {
          key: row.key,
          name: row.name,
          description: row.description,
          promoted: !!row.promoted,
          // integer percent off the tier rates, e.g. 15 → 15%. Absent/invalid → 0.
          discount: this.normalizeDiscount(options.discount),
          // lowest volume the customer may choose
          minimum: this.normalizeMinimum(options.minimum),
          tiers: (options.tiers || []).map(t => ({
            upTo: t.up_to === undefined ? t.upTo : t.up_to,
            rate: t.amount,
          })),
          presets: options.presets || [],
        };
      });

      this.automation = automation;
    },

    /**
     * Billing period defaults to the interval flagged `promoted` by the API, and
     * volume to the first preset — falling back to COPY when neither is present.
     * The default is never below the minimum.
     */
    applyDefaults() {
      const promoted = this.copy.billing.find((b) => {
        const item = this.automation[b.id];
        return item && item.promoted;
      });
      this.state.billing = promoted ? promoted.id : this.copy.billing[0].id;

      const [firstPreset] = this.presets;
      const start = firstPreset === undefined ? this.copy.volume.fallbackPages : firstPreset;
      this.setVolume(Math.max(this.minimumPages, start));
    },

    // =========================================================================
    // SELECTION
    // =========================================================================

    setBilling(id) {
      this.state.billing = id;
    },

    /**
     * Bounds the value to 0…max only. The minimum is NOT forced here — typing a
     * value below it is allowed so the field can show what was entered while
     * `belowMinimum` blocks checkout.
     */
    setVolume(value) {
      const pages = Math.round(Number(value) || 0);
      this.state.pages = Math.max(0, Math.min(this.copy.volume.max, pages));
    },

    // =========================================================================
    // CHECKOUT
    // =========================================================================

    async onCheckout() {
      if (!this.cart || this.submitting) return;

      this.submitting = true;
      this.checkoutError = '';

      try {
        const session = await this.postCheckoutSession(this.cart);

        if (!session || !session.url) {
          throw new Error('No Stripe Checkout URL returned.');
        }

        window.location.href = session.url;
      } catch (error) {
        this.checkoutError = 'Checkout could not be started. Please try again.';
        // eslint-disable-next-line no-console
        console.error('[checkout] failed', error, this.cart);
      } finally {
        this.submitting = false;
      }
    },

    // =========================================================================
    // PRICING HELPERS
    // =========================================================================

    /**
     * `options.discount` is an integer percent off the tier rates (15 → 15%).
     * Anything missing, non-numeric or outside 0–100 is treated as no discount.
     */
    normalizeDiscount(value) {
      const percent = Number(value);
      if (!Number.isFinite(percent) || percent <= 0) return 0;
      return Math.min(100, percent);
    },

    /**
     * `options.minimum` is the lowest number of pages the customer may select.
     * Missing, non-numeric or negative → no minimum. Never exceeds the input max.
     */
    normalizeMinimum(value) {
      const minimum = Number(value);
      if (!Number.isFinite(minimum) || minimum <= 0) return 0;
      return Math.min(this.copy.volume.max, Math.round(minimum));
    },

    /**
     * Returns the tiers with the discount applied to each rate. Rates stay in
     * cents and are NOT rounded here — rounding happens once on the line total,
     * so a fractional per-page rate cannot drift across a large page count.
     */
    applyDiscount(tiers, discountPercent) {
      if (!discountPercent) return tiers;

      const multiplier = 1 - (discountPercent / 100);
      return tiers.map(t => ({ ...t, rate: Math.round( t.rate * multiplier), listRate: t.rate }));
    },

    /** Discounted cost in dollars for one interval at a given volume. */
    costFor(interval, pages) {
      const item = this.automation[interval];
      if (!item) return 0;
      return this.computeCost(pages, this.applyDiscount(item.tiers, item.discount)).cost;
    },

    /**
     * Walks the graduated tiers and returns the per-tier lines plus the monthly
     * cost in dollars. Rates are cents per page.
     */
    computeCost(pages, tiers) {
      let previous = 0;
      let cents = 0;
      const lines = [];

      for (let i = 0; i < tiers.length; i += 1) {
        if (pages <= previous) break;

        const upTo = tiers[i].upTo == null ? pages : tiers[i].upTo;
        const cap = Math.min(pages, upTo);
        const count = cap - previous;

        if (count > 0) {
          const lineCents = count * tiers[i].rate;
          cents += lineCents;
          lines.push({
            from: previous + 1,
            to: cap,
            count,
            rate: tiers[i].rate,
            cost: lineCents / 100,
          });
        }

        previous = upTo;
      }

      return { cost: cents / 100, lines };
    },

    bandLabel(line) {
      return `${this.num(line.from)}–${this.num(line.to)} (${this.num(line.count)} pg)`;
    },

    rateLabel(cents) {
      return `$${(cents / 100).toFixed(2)}/pg`;
    },

    num(value) {
      return Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },

    money(value) {
      const negative = value < 0;
      const absolute = Math.abs(value);
      const fixed = (Math.round(absolute * 100) / 100)
        .toFixed(2)
        .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return `${negative ? '-$' : '$'}${fixed}`;
    },

    money0(value) {
      return `$${this.num(value)}`;
    },
  },
};
</script>

<!-- ============================================================================
     TYPEFACE — see SubscriptionBuilder.vue. This page inherits the app's font
     stack and only names Manrope in case it is ever self-hosted app-wide:

         npm i @fontsource-variable/manrope
         // src/main.js
         import '@fontsource-variable/manrope';
     ========================================================================= -->

<style scoped lang="scss">
/* =============================================================================
   LOCAL DESIGN TOKENS
   Every value falls back to the Claude-design value, so the component looks
   correct even when colors_and_type.css is not loaded. If the app already
   defines these on :root they win, because the fallback only applies when the
   variable is undefined.
   ========================================================================== */
.builder-page {
  --sb-accent: #{$color-primary-blue};
  --sb-accent-hover: #{$color-primary-dark-blue};
  --sb-accent-soft: #{$color-pastel-blue};
  --sb-ink: #{$color-gray-25};

  --sb-fg-primary: #{$global-text-color};
  --sb-fg-secondary: #{$color-gray-55};
  --sb-fg-tertiary: #{$color-gray-6C};

  --sb-border-default: #{$color-gray-E1};
  --sb-border-strong: #{$color-gray-C4};
  --sb-border-subtle: #{$color-gray-E9};

  --sb-success-bg: #{$color-pastel-green};
  --sb-success-fg: #{$color-primary-green};

  /* used for validation + load/checkout failures, so red rather than orange */
  --sb-warn-bg: #{$color-white-peach};
  --sb-warn-fg: #{$color-primary-dark-red};

  --sb-surface: #{$color-white};
  --sb-bg-app: #{$color-gray-F5};
  --sb-bg-subtle: #{$color-gray-F5};

  --sb-shadow-xs: 0 1px 2px #{rgba($color-gray-25, 0.06)};
  --sb-shadow-sm: 0 6px 24px #{rgba($color-gray-25, 0.06)};

  --sb-dur: var(--dur-base, 160ms);
  --sb-ease: var(--ease-out, cubic-bezier(0.22, 0.61, 0.36, 1));

  --sb-font-display: 'Manrope Variable', 'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
  --sb-font-body: 'Manrope Variable', 'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;

  /* --- WEIGHT + TRACKING, tuned for the system stack -----------------------
     Restore the design exactly once Manrope is loaded app-wide:
       --sb-w-display: 800;  --sb-w-strong: 700;
       --sb-track-display: -0.025em;  --sb-track-num: -0.02em;
     ---------------------------------------------------------------------- */
  --sb-w-display: 700;
  --sb-w-strong: 600;
  --sb-track-display: -0.016em;
  --sb-track-num: -0.012em;
  --sb-track-tight: -0.004em;
  --sb-track-caps: 0.1em;
}

*, *::before, *::after { box-sizing: border-box; }

/* ============ FULL-BLEED PAGE ============
   .builder-page paints the app background edge to edge; .wrap only constrains
   the content column. If a parent route container adds its own max-width or
   horizontal padding, uncomment the breakout rules below. */
.builder-page {
  width: 100%;
  min-height: 100%;
  background: var(--sb-bg-app);
  font-family: var(--sb-font-body);
  color: var(--sb-fg-primary);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;

  /* --- breakout: escape a width-constrained / padded parent ---
  margin-left: calc(50% - 50vw);
  margin-right: calc(50% - 50vw);
  */
}

.wrap {
  max-width: 1180px;
  margin: 0 auto;
  padding: 40px 32px 80px;
}
.wrap a { color: var(--sb-accent); text-decoration: none; }
.wrap a:hover { color: var(--sb-accent-hover); }
.wrap img { display: block; max-width: 100%; }

/* ============ HEAD ============ */
.builder-head { text-align: center; max-width: 740px; margin: 0 auto 12px; }
.builder-head .brand { display: flex; justify-content: center; margin-bottom: 22px; }
.builder-head .brand img { height: 26px; width: auto; }
.eyebrow {
  display: inline-flex; align-items: center; gap: 8px;
  font-family: var(--sb-font-body); font-size: 11.5px; font-weight: var(--sb-w-strong);
  text-transform: uppercase; letter-spacing: var(--sb-track-caps); color: var(--sb-accent);
}
.eyebrow .dot { width: 6px; height: 6px; border-radius: 50%; background: var(--sb-accent); }
.h-display {
  font-family: var(--sb-font-display); font-weight: var(--sb-w-display);
  font-size: clamp(28px, 3.4vw, 40px); line-height: 1.12;
  letter-spacing: var(--sb-track-display); margin: 14px 0 12px; color: var(--sb-ink);
}
.h-display .hl { color: var(--sb-accent); }
.lede { font-size: 17px; line-height: 1.55; color: var(--sb-fg-secondary); margin: 0; }

/* ============ STATE NOTES ============ */
.state-note {
  text-align: center; font-size: 14px; color: var(--sb-fg-secondary); margin: 48px 0;
}
.state-note.error { color: var(--sb-warn-fg); }
.state-note.small { text-align: center; font-size: 12.5px; margin: 10px 0 0; }

/* ============ BILLING TOGGLE ============ */
.billing-toggle {
  display: inline-flex; align-items: center; gap: 6px;
  margin-top: 26px; padding: 5px;
  background: var(--sb-surface); border: 1px solid var(--sb-border-default);
  border-radius: 999px; box-shadow: var(--sb-shadow-xs);
}
.billing-opt {
  display: inline-flex; align-items: center; gap: 8px;
  height: 38px; padding: 0 20px; border: none; background: transparent;
  font-family: var(--sb-font-body); font-size: 14px; font-weight: var(--sb-w-strong);
  color: var(--sb-fg-secondary); cursor: pointer; border-radius: 999px;
  transition: color var(--sb-dur) var(--sb-ease), background var(--sb-dur) var(--sb-ease);
}
.billing-opt:hover:not(.active) { color: var(--sb-fg-primary); }
.billing-opt.active { background: var(--sb-ink); color: #fff; }
.billing-opt .save {
  font-size: 11px; font-weight: 700; letter-spacing: 0.02em;
  padding: 2px 8px; border-radius: 999px;
  background: var(--sb-success-bg); color: var(--sb-success-fg);
}
.billing-opt.active .save { background: rgba($color-white, 0.18); color: #fff; }

/* ============ GRID ============ */
.builder-grid {
  display: grid; grid-template-columns: minmax(0, 1fr) 400px;
  gap: 28px; align-items: start; margin-top: 40px;
}

/* ============ STEP ============ */
.step { margin-bottom: 34px; }
.step-head { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; }
.step-title {
  font-family: var(--sb-font-display); font-weight: var(--sb-w-display); font-size: 20px;
  letter-spacing: var(--sb-track-tight); margin: 0; color: var(--sb-ink);
}
.step-sub { font-size: 13.5px; color: var(--sb-fg-secondary); margin: 3px 0 0; }

/* ============ VOLUME ============ */
.vol-card {
  background: var(--sb-surface); border: 1px solid var(--sb-border-default);
  border-radius: 16px; padding: 22px 24px;
}
.vol-top { display: flex; align-items: flex-end; justify-content: space-between; gap: 20px; flex-wrap: wrap; }
.vol-inputwrap { display: flex; align-items: center; gap: 10px; }
.vol-input {
  width: 130px; height: 52px; padding: 0 14px;
  border: 1.5px solid var(--sb-border-default); border-radius: 10px;
  font-family: var(--sb-font-display); font-weight: var(--sb-w-display); font-size: 26px;
  letter-spacing: var(--sb-track-num); color: var(--sb-fg-primary);
  font-variant-numeric: tabular-nums;
  background: var(--sb-surface);
  transition: border-color var(--sb-dur) var(--sb-ease), box-shadow var(--sb-dur) var(--sb-ease);
}
.vol-input:focus { outline: none; border-color: var(--sb-accent); box-shadow: 0 0 0 3px rgba($color-primary-blue, 0.18); }
.vol-input.invalid { border-color: var(--sb-warn-fg); color: var(--sb-warn-fg); }
.vol-input.invalid:focus { box-shadow: 0 0 0 3px rgba($color-primary-red, 0.16); }
.vol-input-lbl { font-size: 13px; color: var(--sb-fg-secondary); line-height: 1.35; }
.vol-input-lbl strong { color: var(--sb-fg-primary); font-weight: var(--sb-w-strong); }
.vol-min-note {
  align-self: center; font-size: 11.5px; color: var(--sb-fg-tertiary);
  font-variant-numeric: tabular-nums;
}
.vol-rate {
  font-size: 12.5px; font-weight: var(--sb-w-strong); color: var(--sb-accent);
  background: var(--sb-accent-soft); padding: 6px 12px; border-radius: 999px;
  display: inline-flex; align-items: center; gap: 6px;
  font-variant-numeric: tabular-nums;
}
.vol-error {
  margin: 14px 0 0; padding: 8px 12px; border-radius: 8px;
  font-size: 12.5px; line-height: 1.45;
  background: var(--sb-warn-bg); color: var(--sb-warn-fg);
}

.vol-slider {
  -webkit-appearance: none; appearance: none; width: 100%; height: 6px; border-radius: 999px;
  background: var(--sb-border-default); margin: 22px 0 14px; cursor: pointer;
}
.vol-slider:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba($color-primary-blue, 0.18); }
.vol-slider::-webkit-slider-thumb {
  -webkit-appearance: none; appearance: none; width: 22px; height: 22px;
  border-radius: 50%; background: var(--sb-accent); border: 3px solid #fff;
  box-shadow: 0 1px 5px rgba($color-gray-25, 0.25); cursor: pointer;
}
.vol-slider::-moz-range-thumb {
  width: 22px; height: 22px; border-radius: 50%; background: var(--sb-accent);
  border: 3px solid #fff; box-shadow: 0 1px 5px rgba($color-gray-25, 0.25); cursor: pointer;
}

.vol-presets { display: flex; gap: 8px; flex-wrap: wrap; }
.vol-preset {
  border: 1px solid var(--sb-border-default); background: var(--sb-surface); cursor: pointer;
  font-family: var(--sb-font-body); font-size: 12.5px; font-weight: var(--sb-w-strong);
  color: var(--sb-fg-secondary); font-variant-numeric: tabular-nums;
  padding: 7px 13px; border-radius: 999px;
  transition: border-color var(--sb-dur) var(--sb-ease), color var(--sb-dur) var(--sb-ease);
}
.vol-preset:hover { border-color: var(--sb-accent); color: var(--sb-accent); }

.vol-tiers { margin-top: 20px; border-top: 1px solid var(--sb-border-subtle); padding-top: 16px; }
.vol-tiers-h {
  font-size: 11px; font-weight: var(--sb-w-strong); letter-spacing: var(--sb-track-caps);
  text-transform: uppercase; color: var(--sb-fg-secondary); margin: 0 0 10px;
}
.vol-tiers-h.no-margin { margin: 0; }
.vol-tiers-h .tiers-note {
  margin-left: 8px; padding: 2px 8px; border-radius: 999px;
  letter-spacing: 0.02em; text-transform: none;
  background: var(--sb-success-bg); color: var(--sb-success-fg);
}
.vt-row {
  display: flex; align-items: center; justify-content: space-between;
  font-size: 13px; padding: 5px 0; font-variant-numeric: tabular-nums;
}
.vt-row .vt-band { color: var(--sb-fg-secondary); }
.vt-row .vt-cost { font-weight: var(--sb-w-strong); color: var(--sb-fg-primary); }

/* ============ SUMMARY ============ */
.summary { position: sticky; top: 24px; }
.summary-card {
  background: var(--sb-surface); border: 1px solid var(--sb-border-default);
  border-radius: 18px; box-shadow: var(--sb-shadow-sm);
  overflow: hidden;
}
.summary-head { padding: 22px 24px 18px; border-bottom: 1px solid var(--sb-border-subtle); }
.summary-head h3 {
  font-family: var(--sb-font-display); font-weight: var(--sb-w-display); font-size: 18px;
  letter-spacing: var(--sb-track-tight); margin: 0; color: var(--sb-ink);
}
.summary-head p { font-size: 12.5px; color: var(--sb-fg-secondary); margin: 4px 0 0; }
.summary-body { padding: 8px 24px 4px; }

.sec-label {
  font-size: 10.5px; font-weight: var(--sb-w-strong); letter-spacing: var(--sb-track-caps);
  text-transform: uppercase; color: var(--sb-fg-tertiary); margin: 16px 0 4px;
}
.li {
  display: flex; align-items: baseline; justify-content: space-between;
  gap: 14px; padding: 8px 0; font-size: 14px;
}
.li .li-name { color: var(--sb-fg-primary); }
.li .li-name .li-sub { display: block; font-size: 11.5px; color: var(--sb-fg-tertiary); margin-top: 2px; }
.li .li-sub .save { color: var(--sb-success-fg); font-weight: 700; }
.li .li-amt {
  font-weight: var(--sb-w-strong); color: var(--sb-fg-primary); white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.li.muted .li-name, .li.muted .li-amt { color: var(--sb-fg-tertiary); font-weight: 500; }

.summary-divider { height: 1px; background: var(--sb-border-subtle); margin: 10px 0; }

.subtotal { display: flex; justify-content: space-between; align-items: baseline; padding: 8px 0; font-size: 14px; }
.subtotal .st-name { color: var(--sb-fg-secondary); font-weight: 500; }
.subtotal .st-amt {
  font-weight: var(--sb-w-strong); color: var(--sb-fg-primary);
  font-variant-numeric: tabular-nums;
}

.total-block { background: var(--sb-bg-subtle); margin: 12px -24px 0; padding: 20px 24px 22px; }
.total-row { display: flex; align-items: flex-end; justify-content: space-between; gap: 12px; }
.total-row .t-label {
  font-family: var(--sb-font-display); font-weight: var(--sb-w-strong); font-size: 15px;
  letter-spacing: var(--sb-track-tight); color: var(--sb-ink);
}
.total-row .t-label small {
  display: block; font-family: var(--sb-font-body); font-weight: 500; font-size: 11.5px;
  color: var(--sb-fg-secondary); margin-top: 2px;
}
.total-row .t-amt {
  font-family: var(--sb-font-display); font-weight: var(--sb-w-display); font-size: 31px;
  letter-spacing: var(--sb-track-num); line-height: 1; color: var(--sb-ink);
  font-variant-numeric: tabular-nums;
}
.total-row .t-amt .per {
  font-family: var(--sb-font-body); font-size: 13px; font-weight: 500; color: var(--sb-fg-secondary);
}
.annual-note { margin-top: 8px; font-size: 12.5px; color: var(--sb-fg-secondary); }
.annual-note .save { color: var(--sb-success-fg); font-weight: 700; }

.summary-foot { padding: 20px 24px 24px; }
.btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  width: 100%; height: 50px; padding: 0 24px;
  border-radius: 11px; border: 1px solid transparent;
  font-family: var(--sb-font-body); font-weight: var(--sb-w-strong); font-size: 15px;
  letter-spacing: 0.005em; cursor: pointer;
  transition: background var(--sb-dur) var(--sb-ease), box-shadow var(--sb-dur) var(--sb-ease), transform var(--sb-dur) var(--sb-ease);
}
.btn .btn-arrow { width: 17px; height: 17px; flex: none; }
.btn[disabled] { opacity: 0.6; cursor: not-allowed; }
.btn-primary { background: var(--sb-accent); color: #fff; }
.btn-primary:hover:not([disabled]) {
  background: var(--sb-accent-hover); transform: translateY(-1px);
  box-shadow: 0 10px 24px rgba($color-primary-blue, 0.3);
}
.btn-skip {
  display: block; width: 100%; margin-top: 12px; text-align: center;
  font-size: 13.5px; font-weight: var(--sb-w-strong); color: var(--sb-fg-secondary);
  transition: color var(--sb-dur) var(--sb-ease);
}
.btn-skip:hover { color: var(--sb-accent); }
.trust { margin-top: 14px; display: flex; flex-direction: column; gap: 8px; }
.trust span { display: inline-flex; align-items: center; gap: 8px; font-size: 12.5px; color: var(--sb-fg-secondary); }
.trust .check {
  width: 16px; height: 16px; border-radius: 50%; flex: none;
  background: var(--sb-success-bg); color: var(--sb-success-fg);
  display: inline-flex; align-items: center; justify-content: center;
}
.trust .check svg { width: 10px; height: 10px; }

.mini-print {
  text-align: center; font-size: 12px; color: var(--sb-fg-tertiary);
  margin-top: 22px; line-height: 1.5;
}
.mini-print a { font-weight: var(--sb-w-strong); }

/* ============ RESPONSIVE ============ */
@media (max-width: 940px) {
  .builder-grid { grid-template-columns: 1fr; }
  .summary { position: static; }
  .summary-card { margin-top: 8px; }
}
@media (max-width: 560px) {
  .wrap { padding: 28px 18px 64px; }
  .vol-top { align-items: flex-start; }
}
</style>
